import { useState, useCallback } from 'react';
import { useCompleteFeedbackRequestMutation, FeedbackRequest } from '@/store/api';
import { useToast } from '@/contexts/admin/ToastContext';
import { NotificationItem } from './useNotifications';

export interface UseMarketplaceFeedbackModalOptions {
  /** Callback invoked after successful feedback submission */
  onSuccess?: () => void;
}

export interface UseMarketplaceFeedbackModalResult {
  /** Whether the feedback modal is visible */
  isVisible: boolean;
  /** The currently selected feedback request */
  feedbackRequest: FeedbackRequest | null;
  /** Whether feedback submission is in progress */
  isSubmitting: boolean;
  /** Open the modal for a given notification */
  openModal: (notification: NotificationItem) => void;
  /** Close the modal and reset state */
  closeModal: () => void;
  /** Submit feedback with rating and optional text */
  submitFeedback: (rating: number, text?: string) => Promise<void>;
}

/**
 * Hook to manage marketplace feedback modal state and actions.
 * 
 * Consolidates the duplicated modal logic from:
 * - ResidentNotificationsScreen
 * - DesktopNotificationsScreen
 * - DesktopNotificationsPopover
 * 
 * @example
 * ```tsx
 * const { isVisible, feedbackRequest, isSubmitting, openModal, closeModal, submitFeedback } = 
 *   useMarketplaceFeedbackModal({ onSuccess: reload });
 * 
 * // In JSX:
 * <LeaveFeedbackModal
 *   visible={isVisible}
 *   feedbackRequest={feedbackRequest}
 *   onClose={closeModal}
 *   onSubmit={submitFeedback}
 *   isSubmitting={isSubmitting}
 * />
 * ```
 */
export const useMarketplaceFeedbackModal = (
  options?: UseMarketplaceFeedbackModalOptions
): UseMarketplaceFeedbackModalResult => {
  const [isVisible, setIsVisible] = useState(false);
  const [feedbackRequest, setFeedbackRequest] = useState<FeedbackRequest | null>(null);
  const [completeFeedbackRequest, { isLoading: isSubmitting }] = useCompleteFeedbackRequestMutation();
  const { showSuccess, showError } = useToast();

  /**
   * Open the feedback modal for a notification.
   * Extracts the feedback request from the notification item.
   */
  const openModal = useCallback((notification: NotificationItem) => {
    const request = notification.feedbackRequest;
    if (request) {
      setFeedbackRequest(request);
      setIsVisible(true);
    }
  }, []);

  /**
   * Close the modal and reset state.
   */
  const closeModal = useCallback(() => {
    setIsVisible(false);
    setFeedbackRequest(null);
  }, []);

  /**
   * Submit feedback for the current feedback request.
   * Shows success/error toast and calls onSuccess callback on success.
   */
  const submitFeedback = useCallback(async (rating: number, text?: string) => {
    if (!feedbackRequest) return;

    try {
      await completeFeedbackRequest({
        requestId: feedbackRequest.id,
        rating,
        text,
      }).unwrap();

      showSuccess('Thank you for your feedback!');
      setIsVisible(false);
      setFeedbackRequest(null);

      // Call onSuccess callback (e.g., to refresh notifications)
      options?.onSuccess?.();
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      showError('Failed to submit feedback. Please try again.');
    }
  }, [feedbackRequest, completeFeedbackRequest, showSuccess, showError, options]);

  return {
    isVisible,
    feedbackRequest,
    isSubmitting,
    openModal,
    closeModal,
    submitFeedback,
  };
};
